/**
 * Coordinate conversion utilities for canvas rendering
 * Handles world-to-screen and screen-to-world transformations
 */

export interface Vec2 {
  x: number;
  y: number;
}

/**
 * Zoom is expressed in pixels-per-meter (set by auto-fit, typically 30–70).
 * DEFAULT_PX_PER_M is the 100% reference for the zoom percentage shown in
 * the toolbar / status bar; MAX_ZOOM_PX_PER_M caps how far in you can zoom.
 */
export const DEFAULT_PX_PER_M = 40;
export const MAX_ZOOM_PX_PER_M = 400;

/** Format a px-per-meter zoom value as a user-facing percentage */
export function zoomToPercent(zoom: number): number {
  return Math.round((zoom / DEFAULT_PX_PER_M) * 100);
}

/**
 * Convert world coordinates (meters) to screen coordinates (pixels)
 */
export function worldToScreen(
  worldPos: Vec2,
  zoom: number,
  panOffset: Vec2
): Vec2 {
  return {
    x: worldPos.x * zoom + panOffset.x,
    y: worldPos.y * zoom + panOffset.y,
  };
}

/**
 * Convert screen coordinates (pixels) to world coordinates (meters)
 */
export function screenToWorld(
  screenPos: Vec2,
  zoom: number,
  panOffset: Vec2
): Vec2 {
  return {
    x: (screenPos.x - panOffset.x) / zoom,
    y: (screenPos.y - panOffset.y) / zoom,
  };
}

/**
 * Snap a world position to the nearest grid point
 */
export function snapToGrid(worldPos: Vec2, gridSize: number): Vec2 {
  return {
    x: Math.round(worldPos.x / gridSize) * gridSize,
    y: Math.round(worldPos.y / gridSize) * gridSize,
  };
}

/**
 * Get the mouse position relative to the canvas
 */
export function getCanvasMousePos(
  e: MouseEvent,
  canvas: HTMLCanvasElement
): Vec2 {
  const rect = canvas.getBoundingClientRect();
  return {
    x: e.clientX - rect.left,
    y: e.clientY - rect.top,
  };
}

/**
 * Calculate distance between two points
 */
export function distanceBetween(a: Vec2, b: Vec2): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Calculate angle between two points in radians
 * Returns angle from point a to point b
 */
export function angleBetween(a: Vec2, b: Vec2): number {
  return Math.atan2(b.y - a.y, b.x - a.x);
}

/**
 * Convert radians to degrees
 */
export function radiansToDegrees(radians: number): number {
  return (radians * 180) / Math.PI;
}

/**
 * Convert degrees to radians
 */
export function degreesToRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

/**
 * Clamp a value between min and max
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/**
 * Check if a point is inside a rectangle
 */
export function pointInRect(
  point: Vec2,
  rectTopLeft: Vec2,
  rectBottomRight: Vec2
): boolean {
  return (
    point.x >= rectTopLeft.x &&
    point.x <= rectBottomRight.x &&
    point.y >= rectTopLeft.y &&
    point.y <= rectBottomRight.y
  );
}

/**
 * Check if a point is inside a circle
 */
export function pointInCircle(
  point: Vec2,
  circleCenter: Vec2,
  radius: number
): boolean {
  return distanceBetween(point, circleCenter) <= radius;
}

/**
 * Get bounding box for a set of points
 */
export function getBoundingBox(
  points: Vec2[]
): { topLeft: Vec2; bottomRight: Vec2 } | null {
  if (points.length === 0) return null;

  let minX = points[0].x;
  let maxX = points[0].x;
  let minY = points[0].y;
  let maxY = points[0].y;

  for (const point of points) {
    minX = Math.min(minX, point.x);
    maxX = Math.max(maxX, point.x);
    minY = Math.min(minY, point.y);
    maxY = Math.max(maxY, point.y);
  }

  return {
    topLeft: { x: minX, y: minY },
    bottomRight: { x: maxX, y: maxY },
  };
}
